import { z } from "zod";

import type { GetChangeRequestDetailQuery, GetChangeRequestsQuery } from "./gqls";

/** Change-request lifecycle statuses (mirrors V1 `searchProps`). `processing` is a
 *  transient display-only state while a batch review job runs. */
export const CHANGE_REQUEST_STATUSES = ["open", "rejected", "committed"] as const;

/** Change-request item model types. `recordMetadata` is labelled "metadata" in the UI. */
export const CHANGE_REQUEST_MODEL_TYPES = ["entity", "relation", "recordMetadata"] as const;

/** Sortable columns of the list. Validated against URL search params. */
const zChangeRequestSortOrder = z.union([
  z.literal("createdAt"),
  z.literal("updatedAt"),
  z.literal("status"),
  z.literal("comments"),
]);
export type ChangeRequestSortOrder = z.infer<typeof zChangeRequestSortOrder>;

const zSortDirection = z.union([z.literal("asc"), z.literal("desc")]);
export type SortDirection = z.infer<typeof zSortDirection>;

/** Review actions a user can submit on a single change request (excludes the batch-only type). */
export const REVIEW_TYPES = ["approve", "reject", "comment"] as const;

/** Review-form schema: a comment is required for reject/comment reviews (V1 parity). */
export const changeRequestReviewFormSchema = z
  .object({
    type: z.enum(REVIEW_TYPES),
    comment: z.string(),
  })
  .refine((data) => data.type === "approve" || data.comment.trim().length > 0, {
    path: ["comment"],
    params: { i18n: "requiredComment" },
    message: "required",
  });
export type ChangeRequestReviewFormData = z.infer<typeof changeRequestReviewFormSchema>;

/** UI-side filter shape resolved from URL params; drives both the query and the sidebar. */
export interface ChangeRequestListFilters {
  statuses: string[];
  modelTypes: string[];
  hasReviewComments: boolean;
  categoryIds: string[];
  dataImportProjectIds: string[];
  search?: string;
  /** Scoping params set via deep-links (not the sidebar) — e.g. from a composer or batch job. */
  recordComposerDefinitionId?: string;
  batchChangeRequestsJobId?: string;
  batchChangeRequestsJobRunId?: string;
}

export type ChangeRequestRaw = GetChangeRequestsQuery["changeRequests"][number];

/** A property shown under a CR row summary (key · truncated value). */
export interface ChangeRequestSummaryProp {
  key: string;
  value: string;
}

/** Condensed summary of a change request's primary item (V1 ChangeRequestSummaryCell parity). */
export interface ChangeRequestSummary {
  modelType: string;
  /** `recordMetadata` → "metadata"; otherwise the raw model type. */
  modelTypeLabel: string;
  operationType: string;
  /** Category names joined with " → " (resolved against the tenant categories). */
  categoryNames: string;
  headline: string;
  secondaryProps: ChangeRequestSummaryProp[];
}

/** UI-ready table row passed from the container to the table. */
export interface ChangeRequestRow {
  id: string;
  status: string;
  createdAt: string;
  commentsCount: number;
  composer: { id: string; name: string; kind: "entity" | "relation" | null; dipId: string | null } | null;
  job: { id: string; name: string; dipId: string | null } | null;
  jobRunAt: string | null;
  summary: ChangeRequestSummary;
}

type ChangeRequestDetailQueryRow = GetChangeRequestDetailQuery["changeRequests"][number];
type ChangeRequestReviewQueryRow = ChangeRequestDetailQueryRow["reviews"][number];

// Hasura can hide a related user even while the tenant's change request remains readable.
export type ChangeRequestDetailRaw = Omit<ChangeRequestDetailQueryRow, "user" | "reviews"> & {
  user: ChangeRequestDetailQueryRow["user"] | null;
  reviews: (Omit<ChangeRequestReviewQueryRow, "user"> & {
    user: ChangeRequestReviewQueryRow["user"] | null;
  })[];
};
export type ChangeRequestItemRaw = ChangeRequestDetailRaw["items"][number];

/** One change-request item prepared for the detail view: header rows, JSON view + old-vs-new diff. */
export interface ChangeRequestItemUI {
  id: string;
  modelType: string;
  modelTypeLabel: string;
  operationType: string;
  /** Target record id (when present) + which V2 detail route it links to. */
  recordId: string | null;
  recordLink: "entity" | "relation" | null;
  /** Locale-resolved display name of the target record (for the detail title). */
  recordName: string;
  /** Record-metadata id (for recordMetadata items). */
  recordMetadataId: string | null;
  deactivatedAt: string | null;
  /** Whether the change request includes `categoryIDs` at all. When false the category
   *  row is hidden — an omitted key means "no category change" (V1 parity), not "remove all". */
  categoryPresent: boolean;
  /** Raw category-id values the change request proposes (shown copyable). */
  categoryIds: string[];
  /** Raw category-id values currently on the record. */
  originalCategoryIds: string[];
  categoryChanged: boolean;
  /** The model's own properties, rendered as a readable key/value table (JSON tab). */
  modelProps: Record<string, unknown>;
  /** Current DB value, fed to the diff viewer as the "old" side (Diff tab). */
  diffOld: unknown;
  /** Proposed value, fed to the diff viewer as the "new" side (Diff tab). */
  diffNew: unknown;
}

/** A review entry shown in the detail review history. */
export interface ChangeRequestReviewRow {
  id: string;
  typeId: string;
  comment: string;
  createdAt: string;
  userName: string;
}

/** A data source shown in Source Info (with an optional Google Sheets link). */
interface ChangeRequestSourceDataSource {
  id: string;
  name: string;
  gsheetUrl: string | null;
}

interface ChangeRequestSourceJob {
  id: string;
  name: string | null;
}

/** Provenance chain shown in the detail Source Info section (V1 ChangeRequestInfo parity). */
interface ChangeRequestSource {
  dataImportProject: { id: string; name: string } | null;
  dataSources: ChangeRequestSourceDataSource[];
  batchEvidencesJobs: ChangeRequestSourceJob[];
  batchChangeRequestsJob: ChangeRequestSourceJob | null;
  jobRunId: string | null;
}

/** One committed item snapshot shown in the detail Commit section. */
interface ChangeRequestCommitItem {
  changeRequestItemId: string;
  properties: unknown;
  createdAt: string;
}

interface ChangeRequestCommit {
  createdAt: string;
  items: ChangeRequestCommitItem[];
}

/** Fully prepared change-request detail for the presenter. */
export interface ChangeRequestDetailUI {
  id: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  context: unknown;
  composerKey: string | null;
  /** The change request's own batch job id (Change Request card). */
  batchChangeRequestsJobId: string | null;
  userName: string;
  evidences: { id: string }[];
  source: ChangeRequestSource;
  items: ChangeRequestItemUI[];
  reviews: ChangeRequestReviewRow[];
  commit: ChangeRequestCommit | null;
}
